(ns hai.admin-ui.users.contacts.table
  "Sidebar Contacts CRUD page."
  (:require
   [clojure.string :as str]
   [com.wsscode.pathom3.connect.operation :as pco]
   [hai.admin-ui.users.contacts.card :as contact-card]
   [hai.journal2.timeline :as timeline]
   [hai.ui.components :as c]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.form :as col.form]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.web-app.layout :as layout]))

(def ContactEditSchema
  [:map
   [:contact/uuid {:identity? true} :uuid]
   [:contact/full-name {:title "Name" :optional true} :string]
   [:contact/github-login {:title "GitHub" :optional true} :string]
   [:contact/linkedin-url {:title "LinkedIn" :optional true} :string]
   [:contact/type
    {:title "Type"
     :form.field.select/options [{:value :contact.type/individual   :title "Individual"}
                                 {:value :contact.type/organisation :title "Organisation"}]
     :optional true}
    [:enum :contact.type/individual :contact.type/organisation]]])

(defn- emails-render
  [{:contact/keys [email]}]
  [table/table-cell
   (table/label {:items (vec (or email []))})])

(defn- emails-domains-render
  [{:contact/keys [org-mailing-namespace]}]
  [table/table-cell
   (table/label {:items (vec (or org-mailing-namespace []))})])

(defn- source-account-render
  [{:contact/keys [auto-filled-from-connection-email]}]
  [table/table-cell
   (if auto-filled-from-connection-email
     [c/Tooltip {:content "Calendar account this contact was auto-added from"}
      [:span auto-filled-from-connection-email]]
     "—")])

(defn- members-render
  [{:contact/keys [members]}]
  [table/table-cell
   (table/label {:items (vec (or members []))
                 :xform (map (fn [m] (or (:contact/full-name m) "Unnamed")))
                 :text "—"})])

(defn- type-render
  [{:contact/keys [type]}]
  [table/table-cell
   (case (or (:db/ident type) type)
     :contact.type/organisation "Organisation"
     :contact.type/individual   "Individual"
     "—")])

(defn- tags-render
  [{:contact/keys [tags]}]
  [table/table-cell
   (if (seq tags)
     (into [:div {:style {:display "flex" :flex-wrap "wrap" :gap "4px"}}]
           (for [{:group/keys [uuid display-name]} tags]
             ^{:key uuid}
             [:span {:style {:background "#DBEAFE" :color "#1E3A5F"
                             :border-radius "9999px" :padding "1px 8px"
                             :font-size "0.75rem" :font-weight "500"}}
              (str "#" (str/replace (or display-name "") #"^#+" ""))]))
     "—")])

(defn table
  []
  [collection/component
   {:id ::table
    :update-mutation 'contact/update
    :schema ContactEditSchema
    :query [{:contacts/table
             [{:collection/items [:contact/uuid
                                  :contact/full-name
                                  :contact/github-login
                                  :contact/linkedin-url
                                  :contact/email
                                  :contact/org-mailing-namespace
                                  (pco/? :contact/auto-filled-from-connection-email)
                                  (pco/? {:contact/type [:db/ident]})
                                  (pco/? {:contact/humble-user [:user/uuid (pco/? :user/full-name)]})
                                  (pco/? {:contact/members [:contact/uuid (pco/? :contact/full-name)]})
                                  (pco/? {:contact/tags [:group/uuid :group/display-name]})]}
              :total :cursor :prev-cursor :next-cursor]}]
    :paginate? true
    :no-items (fn [_] [:em "No contacts yet — add your first contact."])
    :default-view :collection.view/cards
    :card contact-card/contact-card
    :columns [{:header "Name"
               :attr :contact/full-name
               :field (col.form/link-input :contacts/edit)
               :filter (filters/text-search {:placeholder "Name..."})}
              {:header "Type"
               :attr :contact/type
               :field col.form/single-select
               :render type-render
               :sort? false}
              {:header "Emails"
               :attr :contact/email
               :sort? false
               :render emails-render}
              {:header "Email Domains"
               :attr :contact/org-mailing-namespace
               :sort? false
               :render emails-domains-render}
              {:header "Source account"
               :attr :contact/auto-filled-from-connection-email
               :sort? false
               :render source-account-render}
              {:header "GitHub"
               :attr :contact/github-login
               :field col.form/input
               :filter (filters/text-search {:placeholder "GitHub..."})}
              {:header "LinkedIn"
               :attr :contact/linkedin-url
               :field col.form/input
               :sort? false}
              {:header "Members"
               :attr :contact/members
               :sort? false
               :render members-render}
              {:header "Tags"
               :attr :contact/tags
               :sort? false
               :render tags-render}]
    :bulk-actions [{:title "Delete"
                    :mutation 'contact/delete
                    :alert {:content (fn [{:table/keys [selected] :as table*} action]
                                       [bulk-actions/confirm-dialog
                                        {:table  table*
                                         :action action}
                                        [bulk-actions/title "Delete contacts"]
                                        [bulk-actions/description "Are you sure you want to delete the following contacts?"]
                                        (into [:ul]
                                              (map (fn [{:contact/keys [full-name email]}]
                                                     [:li [:strong (or full-name (first email) "Unnamed")]]))
                                              selected)])}}]}])

(defn page
  [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/lead "Your contact book — people and organisations you know. Sources accumulate over time from manual entry and the timeline."]
   [layout/content
    [:div {:style {:display "flex" :align-items "center" :justify-content "flex-end"
                   :margin-bottom "12px"}}
     [button/dark-small
      {:on-click (fn [_] (>e [:contacts/open-add-modal {}]))
       :text "Add contact"
       :icon [icons/plus]}]]
    [table]
    [timeline/add-to-contacts-modal]]])
